import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

export const usePageHeaderAnimation = (pageHeader: Ref<HTMLElement>) => {
    const windowWidth = ref()
    const windowHeight = ref()
    const updatingWindowSize = ref(false)
    const animationContext = gsap.context(() => {})

    if (import.meta.client) {
        gsap.registerPlugin(ScrollTrigger)
    }

    const updateWindowSize = () => {
        if (updatingWindowSize.value) {
            return
        }

        updatingWindowSize.value = true

        requestAnimationFrame(() => {
            windowWidth.value = window.innerWidth
            windowHeight.value = window.innerHeight
            updatingWindowSize.value = false
        })
    }

    const heroMargin = computed(() => {
        if (import.meta.server) {
            return 0
        }

        if (windowWidth.value > 1024) {
            // @screen lg
            return 220
        }

        return 0
    })

    const featureImageSmallWidth = computed(() => {
        if (import.meta.server) {
            return 100
        }

        if (windowWidth.value > 1280) {
            // @screen xl
            return 68.75
        } else if (windowWidth.value > 1024) {
            // @screen lg
            return 87.5
        }

        return 100
    })

    const animationRange = computed(() => {
        const hero = pageHeader.value.querySelector('.page-hero')

        if (!hero) {
            return 0
        }

        const introArea = pageHeader.value.querySelector(
            '.page-intro-area',
        ) as HTMLElement
        const intro = introArea?.querySelector('.page-intro') as HTMLElement

        if (!introArea || !intro) {
            return 0
        }

        const heroImageHeight =
            (hero.clientHeight * featureImageSmallWidth.value) / 100 +
            heroMargin.value
        const introContentHeight = intro.clientHeight
        const introContentTopOffset = intro.parentElement?.offsetTop || 0
        const pageHeaderBottomMargin = getElementStyle(
            pageHeader.value,
            'margin-bottom',
        ) as number

        return Math.round(
            heroImageHeight -
                introContentHeight -
                pageHeaderBottomMargin -
                introContentTopOffset,
        )
    })

    const setupAnimation = () => {
        if (animationRange.value > 0) {
            document.documentElement.style.setProperty(
                '--animation-range',
                `${animationRange.value}px`,
            )
        }

        const body = document.querySelector('body')
        const introArea = document.querySelector('.page-intro-area')
        const intro = introArea?.querySelector('.page-intro')

        if (!introArea || !intro) {
            return
        }

        body?.classList.remove('page-header-scroll')

        animationContext.add(() => {
            gsap.timeline().to(pageHeader.value, {
                keyframes: {
                    '0%': {
                        '--page-header-opacity': 1,
                        '--feature-image-margin': 0,
                        '--feature-image-width': 100,
                    },
                    '40%': {
                        '--feature-image-margin': 0,
                        '--feature-image-width': 100,
                    },
                    '100%': {
                        '--page-header-opacity': 0.2,
                        '--feature-image-margin': heroMargin.value,
                        '--feature-image-width': featureImageSmallWidth.value,
                    },
                },
                scrollTrigger: {
                    trigger: pageHeader.value,
                    start: 'top top',
                    end: `+=${animationRange.value}`,
                    scrub: true,
                    pin: pageHeader.value.querySelector(
                        '.page-intro-area > div',
                    ),
                    onUpdate: (self) => {
                        body?.classList.toggle(
                            'page-header-scroll',
                            self.progress >= 0.8,
                        )
                    },
                },
            })
        })
    }

    onMounted(() => {
        if (!pageHeader.value.querySelector('.page-hero')) {
            // no hero image, bail out
            return
        }

        windowWidth.value = window.innerWidth
        windowHeight.value = window.innerHeight
        window.addEventListener('resize', updateWindowSize)

        setTimeout(() => {
            setupAnimation()
        }, 1000)
    })

    onBeforeUnmount(() => {
        animationContext?.revert()

        document.querySelector('body')?.classList.remove('page-header-scroll')

        window.removeEventListener('resize', updateWindowSize)
        document.removeEventListener('page-transition-finish', setupAnimation)

        // reset the CSS variables
        pageHeader.value.style.setProperty('--page-header-opacity', '1')
        pageHeader.value.style.setProperty('--feature-image-margin', '0')
        pageHeader.value.style.setProperty('--feature-image-width', '100%')
    })
}
